import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { BACKEND_URL } from "../config/supabase.js";
import { thumbUrl } from "../utils/imageUpload.js";
import { Icon, BloomMark } from "./Icon.jsx";

// ─── NAV SEARCH ───────────────────────────────────────────────────────────────
// Lives in the top bar (moved off the Discover page body). Theme-aware via `ns`
// so it reads on both dark and light nav templates. On mobile it collapses to an
// icon that opens a full-width overlay under the nav. Reuses /api/marketplace/search.
export function NavSearch({ ns, isMobile }) {
  const navigate = useNavigate();
  const [q, setQ]             = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);   // desktop dropdown / mobile overlay
  const [tab, setTab]         = useState("products"); // phone / narrow: which result list is showing
  const inputRef = useRef(null);
  const wrapRef  = useRef(null);
  const debRef   = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    clearTimeout(debRef.current);
    if (!q.trim() || q.length < 2) { setResults(null); return; }
    setLoading(true);
    debRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/marketplace/search?q=${encodeURIComponent(q)}`);
        if (r.ok) setResults(await r.json());
      } catch (e) {}
      setLoading(false);
    }, 220);
    return () => clearTimeout(debRef.current);
  }, [q]);

  const goProduct = (p) => { setOpen(false); setQ(""); setResults(null); navigate(`/product/${p.id}`); };
  const goStore   = (s) => { setOpen(false); setQ(""); setResults(null); navigate(`/store/${s.store_slug}`); };

  useEffect(() => { setTab("products"); }, [q]);

  const hasResults = results && (results.products?.length > 0 || results.stores?.length > 0);
  const showDrop   = open && q.length >= 2;
  // Desktop popover is wider than the search box so Products | Stores can sit side by side. It's clamped to the space
  // left of the viewport edge; if that's too tight for two columns it falls back to stacked (phones always stack).
  const room = (!isMobile && showDrop && wrapRef.current && typeof window !== "undefined")
    ? Math.floor(window.innerWidth - wrapRef.current.getBoundingClientRect().left - 16) : 0;
  const sideBySide = !isMobile && room >= 460;
  const dropWidth  = sideBySide ? Math.min(600, room) : undefined;
  const nProd = results?.products?.length || 0, nStore = results?.stores?.length || 0;
  // Not enough room for two columns: one list at a time behind Products | Stores tabs (a single list needs no tabs).
  const tabbed = !sideBySide && nProd > 0 && nStore > 0;
  const showProducts = nProd > 0 && (!tabbed || tab === "products");
  const showStores   = nStore > 0 && (!tabbed || tab === "stores");

  // Shared results dropdown (light popover — readable on any nav theme).
  const Dropdown = (
    <div style={{
      position: "absolute", top: "calc(100% + 8px)", left: 0, right: sideBySide ? "auto" : 0, width: dropWidth, zIndex: 500,
      background: "#fff", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 4,
      overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,0.22)", maxHeight: 400, overflowY: "auto",
    }}>
      {loading && <div style={{ padding: "16px 18px", color: "rgba(0,0,0,0.4)", fontSize: 13 }}>Searching…</div>}
      {!loading && !hasResults && <div style={{ padding: "16px 18px", color: "rgba(0,0,0,0.4)", fontSize: 13 }}>No results for “{q}”</div>}
      {!loading && hasResults && (
        <>
        {tabbed && (
          <div style={{ display: "flex", position: "sticky", top: 0, zIndex: 1, background: "#fff", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
            {[["products", "Products", nProd], ["stores", "Stores", nStore]].map(([id, label, n]) => (
              <button key={id} onClick={() => setTab(id)}
                style={{ flex: 1, padding: "12px 8px", background: "none", border: "none", borderBottom: tab === id ? "2px solid #1a1a1a" : "2px solid transparent", marginBottom: -1, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: tab === id ? 800 : 600, color: tab === id ? "#1a1a1a" : "rgba(0,0,0,0.45)" }}>
                {label} <span style={{ fontWeight: 600, opacity: 0.6 }}>{n}</span>
              </button>
            ))}
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: sideBySide ? "minmax(0,1.5fr) minmax(0,1fr)" : "1fr", alignItems: "start" }}>
          {showProducts && (
            <div style={{ minWidth: 0 }}>
              {!tabbed && <div style={{ padding: "10px 16px 5px", fontSize: 10, fontWeight: 800, color: "rgba(0,0,0,0.32)", letterSpacing: "0.1em", textTransform: "uppercase" }}>Products</div>}
              {results.products.map((p) => {
                const pImg = (Array.isArray(p.images) && p.images.length > 0) ? p.images[0] : (p.img || null);
                const isSold = p.sold || !p.is_live;
                return (
                  <div key={p.id} onClick={() => goProduct(p)}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 16px", cursor: "pointer", opacity: isSold ? 0.6 : 1 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "rgba(0,0,0,0.035)"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                    {pImg
                      ? <img src={thumbUrl(pImg, 100)} alt="" style={{ width: 38, height: 38, borderRadius: 4, objectFit: "cover", flexShrink: 0 }} />
                      : <div style={{ width: 38, height: 38, borderRadius: 4, background: "rgba(47,99,221,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}><BloomMark /></div>}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13.5, color: "#1a1a1a", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: "rgba(0,0,0,0.45)", marginTop: 1 }}>{p.store_name} · ₹{Number(p.price || 0).toLocaleString()}</div>
                    </div>
                    {isSold && <span style={{ fontSize: 9, fontWeight: 800, background: "rgba(180,35,24,0.14)", color: "#B42318", borderRadius: 4, padding: "1px 5px", flexShrink: 0 }}>SOLD</span>}
                  </div>
                );
              })}
            </div>
          )}
          {showStores && (
            <div style={{ minWidth: 0, borderLeft: sideBySide && nProd > 0 ? "1px solid rgba(0,0,0,0.06)" : "none" }}>
              {!tabbed && <div style={{ padding: "10px 16px 5px", fontSize: 10, fontWeight: 800, color: "rgba(0,0,0,0.32)", letterSpacing: "0.1em", textTransform: "uppercase" }}>Stores</div>}
              {results.stores.map((s) => (
                <div key={s.id} onClick={() => goStore(s)}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 16px", cursor: "pointer" }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "rgba(0,0,0,0.035)"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: "rgba(47,99,221,0.16)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}><BloomMark /></div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13.5, color: "#1a1a1a", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{s.store_name}</div>
                    <div style={{ fontSize: 11, color: "#2F63DD", marginTop: 1 }}>Visit store →</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        </>
      )}
    </div>
  );

  // ── Mobile: icon toggles a full-width overlay under the nav ──
  if (isMobile) {
    return (
      <>
        <button onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 40); }}
          aria-label="Search"
          style={{ width: 40, height: 40, borderRadius: "50%", border: `1.5px solid ${ns.border}`, background: "transparent", color: ns.text, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 15, opacity: 0.85 }}><Icon n="search" size={15} inline /></span>
        </button>
        {open && createPortal(
          <div ref={wrapRef} style={{ position: "fixed", top: 56, left: 0, right: 0, zIndex: 500, padding: "10px 14px", background: ns.bg, borderBottom: `1px solid ${ns.border}` }}>
            <div style={{ position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(255,255,255,0.12)", border: `1.5px solid ${ns.border}`, borderRadius: 4, padding: "11px 16px" }}>
                <span style={{ fontSize: 15, opacity: ns.onBlue ? 0.9 : 0.5, color: ns.text }}><Icon n="search" size={15} inline /></span>
                <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
                  placeholder="Search products, stores…"
                  style={{ flex: 1, background: "transparent", border: "none", outline: "none", fontSize: 15, color: ns.text, fontFamily: "inherit", fontWeight: 500 }} />
                <button onClick={() => { setQ(""); setOpen(false); }} style={{ background: "none", border: "none", cursor: "pointer", color: ns.text, opacity: 0.5, fontSize: 20, lineHeight: 1, padding: 0 }}>×</button>
              </div>
              {showDrop && Dropdown}
            </div>
          </div>,
          document.body
        )}
      </>
    );
  }

  // ── Desktop: inline input with dropdown ──
  return (
    <div ref={wrapRef} style={{ position: "relative", flex: 1, maxWidth: 420, minWidth: 140, marginRight: "auto" }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 9,
        background: ns.searchBg || (open ? "rgba(150,150,150,0.16)" : "rgba(150,150,150,0.1)"),
        border: `1.5px solid ${open ? (ns.searchFocus || "#2F63DD") : ns.border}`,
        borderRadius: 4, padding: "8px 15px", transition: "border-color 0.16s, background 0.16s",
      }}>
        <span style={{ fontSize: 14, opacity: ns.onBlue ? 0.9 : 0.5, flexShrink: 0, color: ns.text }}><Icon n="search" size={14} inline /></span>
        <input ref={inputRef} value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === "Escape") { setOpen(false); setQ(""); } }}
          placeholder="Search products, stores…"
          style={{ flex: 1, background: "transparent", border: "none", outline: "none", fontSize: 14, color: ns.text, fontFamily: "inherit", fontWeight: 500, minWidth: 0 }} />
        {q && (
          <button onClick={() => { setQ(""); setResults(null); inputRef.current?.focus(); }}
            style={{ background: "none", border: "none", cursor: "pointer", color: ns.text, opacity: 0.45, fontSize: 18, lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
        )}
      </div>
      {showDrop && Dropdown}
    </div>
  );
}
